import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdDownload,
  MdEmail,
  MdRefresh,
  MdShield,
  MdEdit,
  MdAdd,
  MdDelete,
  MdVisibility,
  MdOutlinePictureAsPdf,
  MdBugReport
} from "react-icons/md";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import axios from "../../../utils/api/axios";
import toast from "react-hot-toast";
import DataDeletionCertificatePDF from "./DataDeletionCertificatePDF";
import { OrganizationOverview } from "../OrganizationsSubscriptions";

interface DeletionCertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  org: OrganizationOverview | null;
  onSuccess?: () => void;
}

export const DeletionCertificateModal: React.FC<DeletionCertificateModalProps> = ({
  isOpen,
  onClose,
  org,
  onSuccess
}) => {
  const [certRef, setCertRef] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [deletionDate, setDeletionDate] = useState("");
  const [engagementName, setEngagementName] = useState("");
  const [reportRef, setReportRef] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [dataDeletedItems, setDataDeletedItems] = useState<string[]>([]);

  // Program selection state
  const [availablePrograms, setAvailablePrograms] = useState<
    Array<{ program_id: number; program_title: string; createdAt?: any }>
  >([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState<boolean>(false);

  const [newItemText, setNewItemText] = useState("");
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const [emailLoading, setEmailLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (org && isOpen) {
      const year = new Date().getFullYear();
      const orgNum = (org.company_id || 1004).toString().padStart(4, "0");

      setCertRef(`CTB-DEL-${year}-${orgNum}`);
      setReportRef(`CTB-RPT-${year - 1}-${orgNum}`);
      setCompanyName(org.company_name || "");

      const todayStr = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });

      setIssueDate(todayStr);

      const computedDelDate = org.last_deletion_notice_sent_at
        ? new Date(org.last_deletion_notice_sent_at).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric"
          })
        : todayStr;

      setDeletionDate(computedDelDate);
      setRecipientEmail(org.contact?.email || "");

      setDataDeletedItems([
        "Pentest report and all associated vulnerability findings",
        "Related engagement data held on the platform",
        "Backups containing the above"
      ]);

      // Populate Programs and default engagementName to program_title
      const initialProgs = org.programs || [];
      if (initialProgs.length > 0) {
        setAvailablePrograms(initialProgs);
        const allIds = initialProgs.map(p => p.program_id);
        setSelectedProgramIds(allIds);
        const titles = initialProgs.map(p => p.program_title).filter(Boolean);
        setEngagementName(
          titles.length > 0
            ? titles.join(", ")
            : `${org.company_name} Penetration Test`
        );
      } else {
        setLoadingPrograms(true);
        axios
          .get(`/v2/companies/admin/${org.company_id}/quick-summary`)
          .then(res => {
            const fetchedProgs = res.data?.programs || [];
            if (fetchedProgs.length > 0) {
              setAvailablePrograms(fetchedProgs);
              setSelectedProgramIds(fetchedProgs.map((p: any) => p.program_id));
              const titles = fetchedProgs.map((p: any) => p.program_title).filter(Boolean);
              if (titles.length > 0) {
                setEngagementName(titles.join(", "));
              }
            } else {
              setEngagementName(
                org.latest_program_title ||
                  org.subscription_name ||
                  `${org.company_name} Penetration Test`
              );
            }
          })
          .catch(() => {
            setEngagementName(
              org.latest_program_title ||
                org.subscription_name ||
                `${org.company_name} Penetration Test`
            );
          })
          .finally(() => {
            setLoadingPrograms(false);
          });
      }
    }
  }, [org, isOpen]);

  const handleToggleProgram = (progId: number) => {
    setSelectedProgramIds(prev => {
      const isSelected = prev.includes(progId);
      const next = isSelected ? prev.filter(id => id !== progId) : [...prev, progId];
      const selected = availablePrograms.filter(p => next.includes(p.program_id));
      if (selected.length > 0) {
        const titles = selected.map(p => p.program_title).filter(Boolean);
        setEngagementName(titles.join(", "));
      } else if (org) {
        setEngagementName(`${org.company_name} Penetration Test`);
      }
      return next;
    });
  };

  const handleSelectAllPrograms = () => {
    if (selectedProgramIds.length === availablePrograms.length) {
      setSelectedProgramIds([]);
      if (org) setEngagementName(`${org.company_name} Penetration Test`);
    } else {
      const allIds = availablePrograms.map(p => p.program_id);
      setSelectedProgramIds(allIds);
      const titles = availablePrograms.map(p => p.program_title).filter(Boolean);
      setEngagementName(titles.join(", "));
    }
  };

  const handleAddItem = () => {
    if (!newItemText.trim()) return;
    setDataDeletedItems(prev => [...prev, newItemText.trim()]);
    setNewItemText("");
  };

  const handleRemoveItem = (index: number) => {
    setDataDeletedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, val: string) => {
    setDataDeletedItems(prev => {
      const updated = [...prev];
      updated[index] = val;
      return updated;
    });
  };

  const pdfDocument = useMemo(
    () => (
      <DataDeletionCertificatePDF
        certRef={certRef}
        issueDate={issueDate}
        companyName={companyName}
        deletionDate={deletionDate}
        engagementName={engagementName}
        reportRef={reportRef}
        dataDeletedItems={dataDeletedItems}
      />
    ),
    [certRef, issueDate, companyName, deletionDate, engagementName, reportRef, dataDeletedItems]
  );

  // Client-side PDF Download using exact form state
  const handleDownloadPDF = async () => {
    setDownloadLoading(true);
    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${companyName.replace(/\s+/g, "_")}_Deletion_Certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Certificate PDF downloaded successfully");
    } catch (error: any) {
      console.error("Error generating PDF download:", error);
      toast.error("Failed to generate certificate PDF");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Send Email with attached PDF using exact form state
  const handleSendEmail = async () => {
    if (!org) return;
    if (!recipientEmail.trim()) {
      toast.error("Recipient email address is required");
      return;
    }

    setEmailLoading(true);
    try {
      const res = await axios.post(`/v2/companies/admin/${org.company_id}/send-deletion-confirmation`, {
        recipient_email: recipientEmail.trim(),
        certRef,
        issueDate,
        companyName,
        deletionDate,
        engagementName,
        program_ids: selectedProgramIds,
        reportRef,
        dataDeletedItems
      });

      toast.success(res.data.message || "Deletion confirmation email sent with attached PDF certificate");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Send deletion confirmation email error:", error);
      toast.error(error?.response?.data?.message || "Failed to send deletion confirmation email");
    } finally {
      setEmailLoading(false);
    }
  };

  if (!isOpen || !org) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh] animate-scale-up">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/70 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-md">
              <MdShield className="text-xl" />
            </div>
            <div>
              <h3 className="font-extrabold text-base text-slate-900 tracking-tight flex items-center gap-2">
                Data Deletion Certificate — Customize & Dispatch
              </h3>
              <p className="text-[11px] text-slate-500 font-medium">
                Verify auto-populated data, customize certificate fields, and send via email or download PDF.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* View Switcher */}
            <div className="flex bg-slate-200/70 p-1 rounded-xl text-xs font-bold">
              <button
                onClick={() => setActiveTab("edit")}
                className={`px-3 py-1 rounded-lg flex items-center gap-1.5 transition ${
                  activeTab === "edit"
                    ? "bg-white text-slate-900 shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <MdEdit className="text-sm" />
                Form Fields
              </button>
              <button
                onClick={() => setActiveTab("preview")}
                className={`px-3 py-1 rounded-lg flex items-center gap-1.5 transition ${
                  activeTab === "preview"
                    ? "bg-white text-slate-900 shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <MdVisibility className="text-sm" />
                Live PDF Preview
              </button>
            </div>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-200/50 transition"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
          {activeTab === "edit" ? (
            <div className="space-y-6">
              {/* Top Banner Notice */}
              <div className="p-3.5 bg-blue-50/70 border border-blue-100 rounded-2xl flex items-center gap-3 text-xs text-blue-900">
                <MdOutlinePictureAsPdf className="text-2xl text-blue-600 shrink-0" />
                <div>
                  <span className="font-bold">Shared Data Synchronization:</span> The values edited below will be rendered into the PDF certificate attachment when sent via email or downloaded directly.
                </div>
              </div>

              {/* Grid 1: Certificate References & Metadata */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-4">
                <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                  Certificate Header & Identification
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">
                      Certificate Reference ID
                    </label>
                    <input
                      type="text"
                      value={certRef}
                      onChange={e => setCertRef(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">
                      Report Reference ID
                    </label>
                    <input
                      type="text"
                      value={reportRef}
                      onChange={e => setReportRef(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">
                      Organization Legal Name
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  {/* Program Selection Box */}
                  <div className="md:col-span-2 bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/90 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MdBugReport className="text-purple-600 text-base" />
                        <span className="font-bold text-xs text-slate-800">
                          Select Programs under this Subscription
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-purple-100 text-purple-700">
                          {selectedProgramIds.length} of {availablePrograms.length} selected
                        </span>
                      </div>
                      {availablePrograms.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllPrograms}
                          className="text-[11px] font-bold text-purple-600 hover:text-purple-800 transition"
                        >
                          {selectedProgramIds.length === availablePrograms.length ? "Deselect All" : "Select All"}
                        </button>
                      )}
                    </div>

                    {loadingPrograms ? (
                      <div className="flex items-center gap-2 text-xs text-slate-400 py-1.5">
                        <MdRefresh className="animate-spin text-sm" />
                        Loading programs...
                      </div>
                    ) : availablePrograms.length === 0 ? (
                      <div className="text-[11px] text-slate-500 bg-white p-2 rounded-lg border border-slate-200/60">
                        No active pentest programs found under this subscription. Default engagement title is applied.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        {availablePrograms.map(p => {
                          const isChecked = selectedProgramIds.includes(p.program_id);
                          return (
                            <div
                              key={p.program_id}
                              onClick={() => handleToggleProgram(p.program_id)}
                              className={`flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer transition select-none ${
                                isChecked
                                  ? "bg-purple-50/90 border-purple-300 text-purple-900 shadow-2xs"
                                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5 pointer-events-none"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-xs truncate">
                                  {p.program_title || `Program #${p.program_id}`}
                                </p>
                                {p.createdAt && (
                                  <p className="text-[10px] text-slate-400">
                                    Created: {new Date(p.createdAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-[10.5px] text-slate-500">
                      Selecting program(s) dynamically syncs the <strong>Engagement / Scope Title</strong> below.
                    </p>
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">
                      Engagement / Scope Title
                    </label>
                    <input
                      type="text"
                      value={engagementName}
                      onChange={e => setEngagementName(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">
                      Date of Issue
                    </label>
                    <input
                      type="text"
                      value={issueDate}
                      onChange={e => setIssueDate(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">
                      Date of Deletion
                    </label>
                    <input
                      type="text"
                      value={deletionDate}
                      onChange={e => setDeletionDate(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Grid 2: Recipient Contact & Email Setup */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-3 text-xs">
                <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <MdEmail className="text-purple-600 text-base" />
                  Recipient Contact Dispatch Email
                </h4>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">
                    Designated Client Contact Email
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={e => setRecipientEmail(e.target.value)}
                    placeholder="client@organization.com"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Grid 3: Data Deleted Items List */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-4 text-xs">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                    Deleted Data Items & Scope Specification
                  </h4>
                  <span className="text-[10px] text-emerald-700 bg-emerald-50 font-bold px-2 py-0.5 rounded border border-emerald-200">
                    {dataDeletedItems.length} Item(s)
                  </span>
                </div>

                <div className="space-y-2">
                  {dataDeletedItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-emerald-600 font-extrabold text-base">•</span>
                      <input
                        type="text"
                        value={item}
                        onChange={e => handleItemChange(idx, e.target.value)}
                        className="flex-1 p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                      <button
                        onClick={() => handleRemoveItem(idx)}
                        className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition"
                        title="Remove Item"
                      >
                        <MdDelete className="text-base" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <input
                    type="text"
                    value={newItemText}
                    onChange={e => setNewItemText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleAddItem())}
                    placeholder="Add custom deleted item specification..."
                    className="flex-1 p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                  <button
                    onClick={handleAddItem}
                    className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs flex items-center gap-1 transition"
                  >
                    <MdAdd className="text-base" />
                    Add Item
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[600px] w-full rounded-2xl border border-slate-200 overflow-hidden bg-slate-800 shadow-inner">
              <PDFViewer width="100%" height="100%" className="rounded-2xl">
                {pdfDocument}
              </PDFViewer>
            </div>
          )}
        </div>

        {/* Footer Action Bar */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
          <div className="text-xs text-slate-500 font-medium">
            Organization ID: <span className="font-bold text-slate-900">#{org.company_id}</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleDownloadPDF}
              disabled={downloadLoading}
              className="px-4 py-2 text-xs font-bold text-slate-900 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 rounded-xl flex items-center gap-2 shadow-2xs transition disabled:opacity-50"
            >
              {downloadLoading ? (
                <MdRefresh className="animate-spin text-base" />
              ) : (
                <MdDownload className="text-base text-emerald-700" />
              )}
              Download PDF
            </button>

            <button
              onClick={handleSendEmail}
              disabled={emailLoading || !recipientEmail}
              className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl flex items-center gap-2 shadow-xs transition disabled:opacity-50"
            >
              {emailLoading ? (
                <MdRefresh className="animate-spin text-base" />
              ) : (
                <MdEmail className="text-base" />
              )}
              Send Email with PDF
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeletionCertificateModal;
